import { flexRender, type Table, type SortingState, type OnChangeFn } from '@tanstack/react-table'
import clsx from 'clsx'
import { type ReactNode } from 'react'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { SortableHeaderCell } from '@/components/sortable-header/SortableHeaderCell'

const kLoadingSkeletonRowCount = 6

/** The name takes the space left by modification time, creator, and actions. */
function WorkProductTableColumns() {
  return (
    <colgroup>
      <col />
      {/* Not max(): a column ignores a width that mixes a percentage with a length. */}
      <col className="hidden w-[11.625rem] [@container_(min-width:_640px)]:table-column [@container_(min-width:_53rem)]:w-[22%]" />
      <col className="w-[35%] [@container_(min-width:_640px)]:w-[22%]" />
      {/* The 28px control plus the design's 12px trailing inset. */}
      <col className="w-10" />
    </colgroup>
  )
}

/** Shared list presentation for matter drafts and tables. */
export function WorkProductTable<T>({
  table,
  onSortingChange,
  onOpenRow,
  isLoading,
  caption,
  statusLabel,
  statusView,
}: {
  table: Table<T>
  onSortingChange: OnChangeFn<SortingState>
  onOpenRow: (row: T) => void
  isLoading: boolean
  caption: string
  statusLabel: string
  statusView: ReactNode
}) {
  const visibleRows = table.getRowModel().rows
  const showStatusView = !isLoading && visibleRows.length === 0

  return (
    <div
      className={clsx(
        'flex min-h-0 w-full flex-col',
        // Reflows on this box, not the viewport: the columns fit the width the table actually has.
        '[container-type:inline-size]'
      )}
    >
      <span role="status" className="sr-only">
        {statusLabel}
      </span>
      {/* One table with a sticky header, not a split header/body pair, so each cell stays tied to its
          header for a screen reader. No overscroll: the bounce drags the sticky header out of view. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-none [scrollbar-color:theme(colors.brand-neutral.400)_transparent]">
        {/* Not `border-collapse`: a collapsed sticky header leaves its borders behind once it detaches. */}
        <table className="w-full table-fixed border-separate border-spacing-0" aria-busy={isLoading}>
          <caption className="sr-only">{caption}</caption>
          <WorkProductTableColumns />
          {/* The `before` patches hide rows scrolling under the rounded corners. */}
          <thead
            className={clsx(
              'sticky top-0 z-10 text-sm font-medium text-text-primary',
              '[&>tr>th]:relative [&>tr>th]:border-y [&>tr>th]:bg-surface-content',
              '[&>tr>th:first-child]:rounded-tl-md [&>tr>th:first-child]:border-l',
              '[&>tr>th:last-child]:rounded-tr-md [&>tr>th:last-child]:border-r',
              "[&>tr>th:first-child]:before:absolute [&>tr>th:first-child]:before:-left-px [&>tr>th:first-child]:before:-top-px [&>tr>th:first-child]:before:-z-10 [&>tr>th:first-child]:before:size-1.5 [&>tr>th:first-child]:before:bg-surface-content [&>tr>th:first-child]:before:content-['']",
              "[&>tr>th:last-child]:before:absolute [&>tr>th:last-child]:before:-right-px [&>tr>th:last-child]:before:-top-px [&>tr>th:last-child]:before:-z-10 [&>tr>th:last-child]:before:size-1.5 [&>tr>th:last-child]:before:bg-surface-content [&>tr>th:last-child]:before:content-['']"
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="h-[42px]">
                {headerGroup.headers.map((header) =>
                  header.column.getCanSort() ? (
                    <SortableHeaderCell key={header.id} header={header} onSortingChange={onSortingChange} />
                  ) : (
                    <th key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  )
                )}
              </tr>
            ))}
          </thead>
          <tbody
            className={clsx(
              'text-sm',
              '[&>tr>td]:border-b',
              '[&>tr>td:first-child]:border-l',
              '[&>tr>td:last-child]:border-r',
              '[&>tr:last-child>td:first-child]:rounded-bl-md [&>tr:last-child>td:last-child]:rounded-br-md'
            )}
          >
            {isLoading ? (
              <WorkProductTableSkeletonRows />
            ) : (
              visibleRows.map((row) => (
                // The row is the control. An interactive cell added inside it has to stop propagation
                // on its own handlers, or it will open the draft too.
                <tr
                  key={row.id}
                  className="h-[60px] cursor-pointer bg-surface-emphasis hover-capable:hover:bg-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring-primary"
                  tabIndex={0}
                  onClick={() => onOpenRow(row.original)}
                  onKeyDown={(event) => {
                    // Both prevented so Space doesn't also scroll the list out from under the
                    // navigation.
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onOpenRow(row.original)
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {showStatusView ? (
          <div className="overflow-hidden rounded-b-md border-x border-b">{statusView}</div>
        ) : (
          <div className="h-[50px] shrink-0" aria-hidden="true" />
        )}
      </div>
    </div>
  )
}

/** Two cells rather than one string, so times line up down the column. */
export function WorkProductTableUpdatedAt({ updatedAt }: { updatedAt: number }) {
  // Locale pinned, not inherited: an ambient one renders 20/05/2026 17:42 for a non-US browser and
  // makes the snapshot and the row test depend on the runner.
  const value = new Date(updatedAt)
  const date = value.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
  const time = value.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })

  return (
    <div className="flex items-center gap-1.5 tabular-nums">
      <span className="w-20 shrink-0">{date}</span>
      <span className="w-16 shrink-0">{time}</span>
    </div>
  )
}

function WorkProductTableSkeletonRows() {
  return (
    <>
      {Array.from({ length: kLoadingSkeletonRowCount }, (_, index) => (
        // LoadingSkeleton sets its own role="status", but it is inert inside this aria-hidden row —
        // the table's own status element owns the announcement.
        <tr key={index} className="h-[60px] bg-surface-emphasis" aria-hidden="true">
          <td className="py-3 pl-5 pr-4 [@container_(min-width:_640px)]:pr-8">
            <div className="flex items-center gap-3">
              {/* Wrapped because the bar stands in for the row icon, and has to leave with it. */}
              <span className="hidden [@container_(min-width:_640px)]:flex">
                <LoadingSkeleton width={18} lineHeight={18} />
              </span>
              <LoadingSkeleton width="66%" lineHeight={16} />
            </div>
          </td>
          {/* pl-1 matches the data cells, or the bars sit 4px left of where the values land. */}
          <td className="hidden py-3 pl-1 pr-8 [@container_(min-width:_640px)]:table-cell">
            <div className="flex">
              <LoadingSkeleton width={128} lineHeight={16} />
            </div>
          </td>
          <td className="py-3 pl-1 pr-2">
            <div className="flex">
              <LoadingSkeleton width={96} lineHeight={16} />
            </div>
          </td>
          <td className="py-3 pr-3" />
        </tr>
      ))}
    </>
  )
}
